import React, { useMemo, useState } from 'react';
import { Modal, Badge, Select } from 'flowbite-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Board, Project } from '../../models';
import type { ColumnData } from '../../models';
import {
    analyzeChanges,
    BOARD_HEALTH_ALL_COLUMNS,
    collectBoardChanges,
    itemsForAction,
    pickDefaultColumnId,
} from '../../utils/boardHealth';
import type { BoardHealthAction, BoardHealthScoredChange } from '../../utils/boardHealth';
import {
    handleChangeMiddleClick,
    preventMiddleClickAutoscroll,
} from '../../utils/openChangeInNewBrowserTab';
import { Icon } from '../Icon';

export type BoardHealthModalProps = {
    show: boolean;
    board?: Board | null;
    project?: Project | null;
    onClose: () => void;
};

type BadgeColor = 'info' | 'success' | 'warning' | 'failure' | 'light';
type HealthView = 'focus' | 'detail' | 'urgent' | 'zombie';

function StatChip(props: { label: string; value: number; color?: BadgeColor }) {
    const { label, value, color = 'light' } = props;
    return (
        <Badge color={color} className="w-fit whitespace-nowrap font-normal">
            <span className="font-normal">{label}</span>
            <span className="ml-1 font-semibold">{value}</span>
        </Badge>
    );
}

function ActionList(props: {
    title: string;
    hint: string;
    items: BoardHealthScoredChange[];
    emptyLabel: string;
    onOpen: (item: BoardHealthScoredChange) => void;
    accent: string;
    meta?: (item: BoardHealthScoredChange) => string;
}) {
    const { title, hint, items, emptyLabel, onOpen, accent, meta } = props;
    const { t } = useTranslation();

    const defaultMeta = (item: BoardHealthScoredChange) => ([
        t(`projects.board_health_detail_${item.detail}`),
        item.ageDays != null
            ? t('projects.board_health_age_days', { count: item.ageDays })
            : null,
        item.change.estimate != null
            ? t('projects.board_health_estimate_hours_short', { count: item.change.estimate })
            : null,
    ].filter(Boolean).join(' · '));

    return (
        <section className="min-w-0 flex-1">
            <h4 className={`text-sm font-semibold ${accent}`}>
                {title}
                <span className="ml-1 font-normal text-gray-500 dark:text-gray-400">({items.length})</span>
            </h4>
            <p className="mb-2 text-xs text-gray-500 dark:text-gray-400">{hint}</p>
            {items.length === 0 ? (
                <p className="text-xs text-gray-400">{emptyLabel}</p>
            ) : (
                <ul className="max-h-64 space-y-1 overflow-y-auto pr-1">
                    {items.map((item) => (
                        <li key={item.change._id}>
                            <button
                                type="button"
                                className="w-full rounded border border-gray-200 px-2 py-1.5 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-900 dark:border-gray-600 dark:hover:bg-gray-700"
                                onClick={() => onOpen(item)}
                                onMouseDown={preventMiddleClickAutoscroll}
                                onAuxClick={(event) => handleChangeMiddleClick(event, item.change)}
                            >
                                <span className="font-mono text-xs text-cyan-700 dark:text-cyan-400">
                                    {item.slugLabel}
                                </span>
                                <span className="ml-2 text-gray-800 dark:text-gray-100">
                                    {item.title}
                                </span>
                                <span className="mt-0.5 block text-[11px] text-gray-500 dark:text-gray-400">
                                    {(meta || defaultMeta)(item)}
                                </span>
                            </button>
                        </li>
                    ))}
                </ul>
            )}
        </section>
    );
}

function BoardHealthModal(props: BoardHealthModalProps) {
    const { show, board, project, onClose } = props;
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();

    const columns = board?.columns || [];
    // null = default (Backlog); '' = whole board; otherwise a column id
    const [columnId, setColumnId] = useState<string | null>(null);
    const [view, setView] = useState<HealthView>('focus');

    const boardChanges = useMemo(() => collectBoardChanges(columns), [columns]);

    const effectiveColumnId = useMemo(() => {
        if (columnId === BOARD_HEALTH_ALL_COLUMNS)
            return BOARD_HEALTH_ALL_COLUMNS;
        if (columnId && columns.some((column) => column._id === columnId))
            return columnId;
        return pickDefaultColumnId(columns) ?? BOARD_HEALTH_ALL_COLUMNS;
    }, [columnId, columns]);

    const selectedColumn: ColumnData | undefined = useMemo(
        () => columns.find((column) => column._id === effectiveColumnId),
        [columns, effectiveColumnId],
    );

    const scopedChanges = useMemo(() => {
        if (effectiveColumnId === BOARD_HEALTH_ALL_COLUMNS)
            return boardChanges;
        return selectedColumn?.changes || [];
    }, [effectiveColumnId, boardChanges, selectedColumn]);

    const report = useMemo(
        () => analyzeChanges(scopedChanges, i18n.language),
        [scopedChanges, i18n.language],
    );

    const detailLists: Array<{ action: BoardHealthAction; accent: string }> = [
        { action: 'delete', accent: 'text-red-700 dark:text-red-400' },
        { action: 'enrich', accent: 'text-amber-700 dark:text-amber-400' },
        { action: 'keep', accent: 'text-emerald-700 dark:text-emerald-400' },
    ];

    const views: Array<{ id: HealthView; label: string; count: number }> = [
        { id: 'focus', label: t('projects.board_health_view_focus'), count: report.doNow.length },
        { id: 'detail', label: t('projects.board_health_view_detail'), count: report.byAction.delete + report.byAction.enrich },
        { id: 'urgent', label: t('projects.board_health_view_urgent'), count: report.urgent.length },
        { id: 'zombie', label: t('projects.board_health_view_zombie'), count: report.zombies.length },
    ];

    const openChange = (item: BoardHealthScoredChange) => {
        const targetProject = project || board?.project;
        if (!targetProject)
            return;
        onClose();
        navigate(Project.path(targetProject, 'change', item.change._id));
    };

    const focusMeta = (item: BoardHealthScoredChange) => ([
        t(`projects.board_health_value_${item.value}`),
        t(`projects.board_health_effort_${item.effort}`),
        item.change.estimate != null
            ? t('projects.board_health_estimate_hours_short', { count: item.change.estimate })
            : null,
        t('projects.board_health_priority_short', { value: item.change.priority ?? 0 }),
    ].filter(Boolean).join(' · '));

    return (
        <Modal show={show} size="4xl" dismissible onClose={onClose}>
            <Modal.Header>
                <Icon type="chart" className="inline mr-1.5" size={5} />
                {t('projects.board_health_title')}
            </Modal.Header>
            <Modal.Body>
                <div className="space-y-4">
                    <p className="text-sm text-gray-600 dark:text-gray-300">
                        {t('projects.board_health_intro')}
                    </p>

                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                        <label className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                            {t('projects.board_health_column')}
                        </label>
                        <Select
                            sizing="sm"
                            className="sm:max-w-xs"
                            value={effectiveColumnId}
                            onChange={(e) => setColumnId(e.target.value)}
                        >
                            <option value={BOARD_HEALTH_ALL_COLUMNS}>
                                {t('projects.board_health_all_columns')} ({boardChanges.length})
                            </option>
                            {columns.map((column) => (
                                <option key={column._id} value={column._id}>
                                    {column.title} ({column.changes?.length || 0})
                                </option>
                            ))}
                        </Select>
                    </div>

                    {columns.length === 0 ? (
                        <p className="text-sm text-gray-500 dark:text-gray-400">{t('projects.empty_board')}</p>
                    ) : (
                        <>
                            <div className="flex flex-wrap gap-2">
                                <StatChip
                                    label={t('projects.board_health_total')}
                                    value={report.total}
                                    color="info"
                                />
                                <StatChip
                                    label={t('projects.board_health_do_now')}
                                    value={report.doNow.length}
                                    color="success"
                                />
                                <StatChip
                                    label={t('projects.board_health_quick')}
                                    value={report.quickWins.length}
                                    color="info"
                                />
                                <StatChip
                                    label={t('projects.board_health_detail_empty')}
                                    value={report.byDetail.empty}
                                    color="failure"
                                />
                                <StatChip
                                    label={t('projects.board_health_estimate_missing')}
                                    value={report.estimateMissing}
                                />
                            </div>

                            <div className="flex flex-wrap gap-1">
                                {views.map((entry) => (
                                    <button
                                        key={entry.id}
                                        type="button"
                                        className={`rounded-lg border px-2.5 py-1 text-xs ${
                                            view === entry.id
                                                ? 'border-cyan-700 bg-cyan-700 text-white'
                                                : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100'
                                        }`}
                                        onClick={() => setView(entry.id)}
                                    >
                                        {entry.label}
                                        <span className="ml-1 opacity-80">({entry.count})</span>
                                    </button>
                                ))}
                            </div>

                            {view === 'focus' && (
                                <div className="flex flex-col gap-4 lg:flex-row">
                                    <ActionList
                                        title={t('projects.board_health_do_now')}
                                        hint={t('projects.board_health_do_now_hint')}
                                        items={report.doNow}
                                        emptyLabel={t('projects.board_health_empty_list')}
                                        onOpen={openChange}
                                        accent="text-cyan-700 dark:text-cyan-400"
                                        meta={focusMeta}
                                    />
                                    <ActionList
                                        title={t('projects.board_health_quick')}
                                        hint={t('projects.board_health_quick_hint')}
                                        items={report.quickWins}
                                        emptyLabel={t('projects.board_health_empty_list')}
                                        onOpen={openChange}
                                        accent="text-indigo-700 dark:text-indigo-400"
                                        meta={focusMeta}
                                    />
                                </div>
                            )}

                            {view === 'detail' && (
                                <>
                                    <div className="flex flex-wrap gap-2 text-xs text-gray-600 dark:text-gray-300">
                                        <span>{t('projects.board_health_age')}:</span>
                                        <StatChip label={t('projects.board_health_age_lt90')} value={report.byAge.lt90} />
                                        <StatChip label={t('projects.board_health_age_90_365')} value={report.byAge.d90_365} />
                                        <StatChip label={t('projects.board_health_age_1_2')} value={report.byAge.y1_2} />
                                        <StatChip label={t('projects.board_health_age_gt2')} value={report.byAge.gt2y} />
                                    </div>
                                    <div className="flex flex-col gap-4 lg:flex-row">
                                        {detailLists.map(({ action, accent }) => (
                                            <ActionList
                                                key={action}
                                                title={t(`projects.board_health_action_${action}`)}
                                                hint={t(`projects.board_health_action_${action}_hint`)}
                                                items={itemsForAction(report, action)}
                                                emptyLabel={t('projects.board_health_empty_list')}
                                                onOpen={openChange}
                                                accent={accent}
                                            />
                                        ))}
                                    </div>
                                </>
                            )}

                            {view === 'urgent' && (
                                <ActionList
                                    title={t('projects.board_health_urgent')}
                                    hint={t('projects.board_health_urgent_hint')}
                                    items={report.urgent}
                                    emptyLabel={t('projects.board_health_empty_list')}
                                    onOpen={openChange}
                                    accent="text-orange-700 dark:text-orange-400"
                                    meta={focusMeta}
                                />
                            )}

                            {view === 'zombie' && (
                                <ActionList
                                    title={t('projects.board_health_zombie')}
                                    hint={t('projects.board_health_zombie_hint')}
                                    items={report.zombies}
                                    emptyLabel={t('projects.board_health_empty_list')}
                                    onOpen={openChange}
                                    accent="text-gray-700 dark:text-gray-300"
                                />
                            )}
                        </>
                    )}
                </div>
            </Modal.Body>
            <Modal.Footer>
                <button
                    type="button"
                    className="inline-flex items-center gap-1 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
                    onClick={onClose}
                >
                    <Icon type="close" size={4} />
                    {t('common.close')}
                </button>
            </Modal.Footer>
        </Modal>
    );
}

export default BoardHealthModal;
